"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-accent/10 border border-accent rounded-lg text-center">
        <p className="text-accent text-lg font-semibold mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-accent text-accent hover:bg-accent/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white">Name</FormLabel>
            <FormControl>
              <input {...field} type="text" placeholder="Your full name" className="w-full px-4 py-3 bg-dark-background border border-muted rounded-md text-white placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none transition-all duration-[var(--duration-medium)] focus:-translate-y-0.5" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white">Email</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="you@example.com" className="w-full px-4 py-3 bg-dark-background border border-muted rounded-md text-white placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none transition-all duration-[var(--duration-medium)] focus:-translate-y-0.5" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white">Phone</FormLabel>
            <FormControl>
              <input {...field} type="tel" placeholder="+1 (555) 123-4567" className="w-full px-4 py-3 bg-dark-background border border-muted rounded-md text-white placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none transition-all duration-[var(--duration-medium)] focus:-translate-y-0.5" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white">Message</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us about your project..." rows={5} className="w-full px-4 py-3 bg-dark-background border border-muted rounded-md text-white placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none transition-all duration-[var(--duration-medium)] focus:-translate-y-0.5 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500 rounded-md">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-white hover:bg-accent-hover active:bg-accent-active focus-visible:ring-2 focus-visible:ring-accent/50 transition-all duration-[var(--duration-medium)] hover:-translate-y-0.5 active:translate-y-0 relative overflow-hidden group">
        <span className="relative z-10">
          {isSubmitting ? <>
              <span className="inline-block animate-spin mr-2"></span>
              Sending...
            </> : "Send Message"}
        </span>
        <span className="absolute inset-0 bg-white/20 translate-y-full group-active:translate-y-0 transition-transform duration-200" />
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <section ref={ref} className="relative py-20 px-4 sm:px-6 lg:px-8 bg-dark-background text-foreground-dark overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl" />
      </div>

      <div className={`relative max-w-2xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-10">
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-muted-light">
            Have a question or project in mind? We'd love to hear from you.
          </p>
        </div>

        <FormProvider className="bg-dark-background/60 backdrop-blur-sm p-8 rounded-lg border border-muted/20 shadow-strong" formId="contact-form-main" sectionName="contact-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours." errorMessage="Something went wrong. Please try again or contact us directly.">
          <ContactFormContent />
        </FormProvider>
      </div>
    </section>;
}